import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";

export function BankruptcyCheckCard({ matterId, showNotification }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  // MFA prompt — defensive only. Current PACER account has no MFA, so this
  // path almost never fires. Kept so the card still works if PACER ever
  // turns MFA on for the firm's account.
  const [mfaPrompt, setMfaPrompt] = useState(null);
  const [mfaCode, setMfaCode] = useState("");

  const load = useCallback(() => {
    apiFetch(`/api/matters/${matterId}/bankruptcy-checks`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { console.error("Bankruptcy checks load failed:", e); setLoading(false); });
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  // Phase is bookkeeping only — invisible to the user. Run intake first,
  // then pre-disbursement once that's done. After both are done, default
  // back to intake (re-run for any reason).
  const nextPhase = () => {
    if (!data?.intake_done) return "intake";
    if (!data?.pre_disbursement_done) return "pre_disbursement";
    return "intake";
  };

  const runAutoSearch = async (phase, smsCode = null) => {
    setSearching(true);
    setSearchResult(null);
    try {
      const resp = await apiFetch(
        `/api/matters/${matterId}/bankruptcy-checks/run-search`,
        {
          method: "POST",
          body: JSON.stringify({ check_phase: phase, sms_code: smsCode }),
        },
      );
      setSearchResult(resp);
      if (resp.mfa_required && !resp.success) {
        setMfaPrompt({ phase, method: resp.mfa_method || "sms" });
        setMfaCode("");
        showNotification("PACER needs a one-time code — enter it to continue.", "warning");
      } else if (resp.inconclusive) {
        showNotification(resp.message || "Search was inconclusive.", "warning");
      } else if (resp.check?.is_in_bankruptcy) {
        showNotification("CLIENT FLAGGED IN BANKRUPTCY — notify the responsible attorney.", "warning");
        load();
      } else {
        showNotification("Bankruptcy check complete — clear.", "success");
        load();
      }
    } catch (e) {
      showNotification("Search failed: " + e.message, "error");
    }
    setSearching(false);
  };

  const submitMfaCode = async () => {
    if (!mfaPrompt || !mfaCode.trim()) return;
    const phase = mfaPrompt.phase;
    const code = mfaCode.trim();
    setMfaPrompt(null);
    setMfaCode("");
    await runAutoSearch(phase, code);
  };

  if (loading) return null;

  const cardStyle = {
    background:"#fff", borderRadius:"12px", padding:"20px 24px",
    border:`1px solid ${C.warmGray}`, marginBottom:"20px",
  };

  return React.createElement("div", { style: cardStyle },
    React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" } },
      React.createElement("h2", { style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, margin:0 } },
        "PACER Bankruptcy Check"
      ),
      React.createElement("button", {
        onClick: () => runAutoSearch(nextPhase()),
        disabled: searching,
        title: "Automated PACER check — searches by client SSN",
        style: {
          padding:"6px 14px", borderRadius:"8px", border:"none",
          background: searching ? C.warmGray : C.gold, color:"#fff",
          fontSize:"12px", fontWeight:600, cursor: searching ? "default" : "pointer",
        }
      }, searching ? "Searching PACER..." : "Run PACER check"),
    ),
    // Auto-search result summary — three verdict tiers:
    //   1. OPEN bankruptcy currently       → red
    //   2. No current case, but history    → gold (closed/dismissed/discharged)
    //   3. CLEAR (never)                   → green
    // Inconclusive (PACER auth/HTTP error) → gold "see message"
    (() => {
      if (!searchResult || mfaPrompt) return null;

      const isInBk = searchResult.check?.is_in_bankruptcy;
      const inconclusive = searchResult.inconclusive;
      const openCount = searchResult.pacer?.open_count ?? 0;
      const histCount = searchResult.pacer?.historical_count ?? 0;
      const hasHistory = !isInBk && !inconclusive && histCount > 0;

      const verdictColor = isInBk
        ? C.error
        : (inconclusive || hasHistory) ? C.gold : C.success;
      const verdictBg = `${verdictColor}12`;
      const verdictBorder = `${verdictColor}30`;

      let verdictLabel;
      if (isInBk) {
        verdictLabel = "OPEN BANKRUPTCY FOUND";
      } else if (inconclusive) {
        verdictLabel = "Inconclusive — see message";
      } else if (hasHistory) {
        verdictLabel = `No current bankruptcy — ${histCount} historical case${histCount === 1 ? "" : "s"} found`;
      } else {
        verdictLabel = "✓ Clear — no bankruptcy history";
      }

      return React.createElement("div", {
        style: {
          marginTop:"12px", padding:"10px 14px", borderRadius:"8px",
          background: verdictBg,
          border: `1px solid ${verdictBorder}`,
          fontSize:"12px",
        }
      },
        React.createElement("div", { style: { fontWeight:700, color: verdictColor, marginBottom:"4px" } },
          verdictLabel,
        ),
        React.createElement("div", { style: { color:C.textMuted } }, searchResult.message),

        // Three-line breakdown when any cases exist (open or historical)
        (openCount > 0 || histCount > 0) && React.createElement("div", {
          style: { marginTop: "6px", fontSize: "11px", color: C.textMuted },
        },
          `Total filings ever: ${openCount + histCount}  ·  Currently open: ${openCount}  ·  Historical: ${histCount}`,
        ),

        searchResult.pacer?.hits?.length > 0 && React.createElement("details", { style: { marginTop:"6px", cursor:"pointer" } },
          React.createElement("summary", null, `PACER hits: ${searchResult.pacer.hit_count}`),
          searchResult.pacer.hits.slice(0, 10).map((h, i) =>
            React.createElement("div", { key: i, style: { fontSize:"11px", color:C.textMuted, marginLeft:"14px", marginTop:"2px" } },
              `${h.case_name || "?"} — ${h.court} ${h.case_number}`,
              h.status === "open"
                ? React.createElement("strong", { style: { color: C.error } }, " (OPEN)")
                : React.createElement("span", { style: { color: C.textLight } }, ` (${(h.status || "closed").toUpperCase()}${h.chapter ? `, Ch ${h.chapter}` : ""})`),
            )
          ),
        ),
        searchResult.receipt?.saved && React.createElement("div", {
          style: { marginTop:"8px", fontSize:"11px", color:C.success, fontWeight:600 }
        },
          `Receipt saved to matter: ${searchResult.receipt.filename}`,
        ),
      );
    })(),
    // MFA prompt inline (defensive — almost never fires)
    mfaPrompt && React.createElement("div", {
      style: {
        marginTop:"12px", padding:"12px 14px", borderRadius:"8px",
        background:`${C.gold}10`, border:`1px solid ${C.gold}40`,
      }
    },
      React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.navy, marginBottom:"6px" } },
        mfaPrompt.method === "totp" ? "Enter your PACER authenticator code" : "Enter the PACER SMS code",
      ),
      React.createElement("div", { style: { display:"flex", gap:"8px", alignItems:"center" } },
        React.createElement("input", {
          type:"text", value: mfaCode, inputMode:"numeric", maxLength: 8,
          onChange: e => setMfaCode(e.target.value.replace(/[^0-9]/g, "")),
          placeholder:"123456",
          autoFocus: true,
          style: { flex:"0 0 120px", padding:"8px 10px", fontSize:"14px", letterSpacing:"0.2em", textAlign:"center", borderRadius:"6px", border:`1px solid ${C.warmGray}` },
        }),
        React.createElement("button", {
          onClick: submitMfaCode,
          disabled: !mfaCode.trim() || searching,
          style: {
            padding:"8px 16px", borderRadius:"6px", border:"none",
            background: !mfaCode.trim() ? C.warmGray : C.navy, color:"#fff",
            fontSize:"13px", fontWeight:600, cursor: !mfaCode.trim() ? "default" : "pointer",
          }
        }, searching ? "..." : "Submit"),
        React.createElement("button", {
          onClick: () => { setMfaPrompt(null); setMfaCode(""); },
          style: { padding:"8px 12px", borderRadius:"6px", border:"none", background:"transparent", fontSize:"12px", color:C.textMuted, cursor:"pointer" }
        }, "Cancel"),
      ),
    ),
  );
}


